<template>
  <div class="pptx-preview" :style="{ height: height }">
    <!-- 舞台：负责居中；容器按课件原始坐标系固定尺寸，靠 transform 整体缩放到舞台大小 -->
    <div ref="stage" class="pptx-stage">
      <div ref="container" class="pptx-container" :style="containerStyle"></div>
    </div>
    <div v-if="loading" class="pptx-mask">
      <i class="el-icon-loading"></i>
      <span>PPT 解析中，请稍候...</span>
    </div>
    <div v-else-if="errorMsg" class="pptx-mask pptx-mask-error">
      <i class="el-icon-warning-outline"></i>
      <span class="pptx-err-text">{{ errorMsg }}</span>
      <el-button type="primary" size="mini" @click="reload">重试</el-button>
    </div>
  </div>
</template>

<script>
/**
 * pptx 课件预览（纯前端解析 OOXML，无第三方服务依赖）
 *
 * 为什么用 pptx-preview 而不是 pptx-viewer：
 * <p>
 * pptx-viewer 0.2.2 只做「把 sp/pic 按 xfrm 摆到页面上」这一件事，缺少三样课件里最常见的结构，
 * 复杂课件必然出现「缺元素 / 错位 / 重叠」：
 * <p>
 * 1. 组合形状（p:grpSp）不展开子坐标系：子形状的坐标是相对 chOff/chExt 的，它直接当页面坐标用 → 整组错位；
 * 2. 不展开 mc:AlternateContent：图形、SmartArt、图表这类元素都装在 Choice/Fallback 里，它看不到 → 整块消失；
 * 3. 不继承母版/版式：母版和版式上的背景、logo、占位符样式全丢 → 缺元素、标题跑到角落。
 * <p>
 * pptx-preview 用的是 PPTist 那套渲染引擎，组合、图表、SmartArt、表格、母版/版式/主题色、
 * 渐变、图片裁剪都覆盖，且逐页渲染可控（renderSingleSlide），正好对上「AI 上课按页推课件」的用法。
 */
import { init } from 'pptx-preview';

// 传给渲染库的基准画布宽度：库内部按 viewPort.width / pptx.width 算缩放比例，
// 传 960 时绝大多数 16:9 课件正好 1:1（DOM 坐标即 PPT 原生坐标），再靠外层 transform 缩放到容器
const BASE_WIDTH = 960;

export default {
  name: 'PptxPreview',
  props: {
    // 课件地址（oss 直链，需允许跨域读取）
    src: {
      type: String,
      default: ''
    },
    // 当前页码，1 开始
    pageNumber: {
      type: Number,
      default: 1
    },
    height: {
      type: String,
      default: '420px'
    },
    // 是否显示翻页控件（同时控制键盘翻页）
    // AI 上课学生端传 false：学生不允许手动翻页，翻页由 AI 端通过 pageNumber 属性远程控制
    showControls: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      previewer: null,
      loading: false,
      errorMsg: '',
      slideCount: 0,
      // 已经真正渲染出来的页码（1 开始），0 表示还没渲染过
      renderedPage: 0,
      // 加载序号：loadViewer 是异步的，src 连续变化时旧的那次会晚于新的返回，
      // 靠它识别出过期结果并丢弃，避免用旧课件覆盖新课件
      loadSeq: 0,
      // 课件原始坐标系下的渲染尺寸（由库算出来，不同比例课件不一样，不能写死 16:9）
      baseWidth: 0,
      baseHeight: 0,
      // 容器 -> 舞台的等比缩放系数
      scale: 1,
      // 容器尺寸监听：窗口/分栏变化时只重算缩放，不重新解析课件
      resizeObserver: null,
      resizeTimer: null
    };
  },
  computed: {
    /**
     * 容器样式：尺寸固定为课件原始渲染尺寸，仅靠 transform 缩放并居中
     * <p>
     * 这样做的好处是缩放不改变内部 DOM 任何数值，文字是矢量渲染，放大到投屏尺寸也不糊；
     * 而且容器尺寸变化时不用重新解析 pptx，只改一个 scale 即可。
     */
    containerStyle() {
      if (!this.baseWidth || !this.baseHeight) {
        return { display: 'none' };
      }
      return {
        width: this.baseWidth + 'px',
        height: this.baseHeight + 'px',
        transform: `translate(-50%, -50%) scale(${this.scale})`
      };
    }
  },
  watch: {
    src() {
      this.loadViewer();
    },
    // 外部页码变化 -> 同步到预览器（换页只重渲染当前页，不重新解析整个文件）
    pageNumber(val) {
      // 等一帧再翻：页码常常和后端下发的其它状态同批更新，让 Vue 先把这批变更铺完，
      // 避免在 DOM 尚未更新时操作预览器
      this.$nextTick(() => {
        this.goTo(val);
      });
    },
    // 控件开关变化 -> 立刻同步显隐（例如中途切换成学生端只读模式）
    showControls() {
      this.syncBuiltinControls();
    }
  },
  mounted() {
    if (this.src) {
      this.loadViewer();
    }
    this.setupResizeObserver();
  },
  beforeDestroy() {
    this.teardownResizeObserver();
    this.destroyViewer();
  },
  methods: {
    /**
     * 下载课件并建立预览器
     * <p>
     * 库只接受 ArrayBuffer，需要自己先 fetch；失败可能是跨域被拒、地址失效或文件不是 pptx。
     */
    async loadViewer() {
      if (!this.src || !this.$refs.container) {
        return;
      }
      this.destroyViewer();
      this.loading = true;
      this.errorMsg = '';
      // 每次进入都取一个新序号，await 回来后若已被更新的加载超过，说明本次结果已过期
      const seq = ++this.loadSeq;
      try {
        const buffer = await this.fetchArrayBuffer(this.src);
        const previewer = init(this.$refs.container, {
          width: BASE_WIDTH,
          // 不传 height：传了会给 wrapper 加上固定高度和 overflow-y，
          // 遇到 4:3 之类的课件会被裁掉或顶出滚动条，尺寸交给 containerStyle 统一管
          mode: 'slide'
        });
        await previewer.preview(buffer);
        if (seq !== this.loadSeq) {
          // 已被更新的加载取代：销毁这次建出来的预览器，别让它把新课件顶掉
          try { previewer.destroy(); } catch (e) { /* 销毁异常不影响主流程 */ }
          return;
        }
        this.previewer = previewer;
        // 库自己算出的渲染尺寸最准（含非 16:9 课件），据此定基准尺寸
        this.applyRenderPort();
        // 学生端不允许手动翻页：关掉控件时把库塞进 wrapper 的「上一页/下一页」按钮和页码一并去掉
        this.syncBuiltinControls();
        // 必须先记下总页数再翻页：goTo 用 slideCount 做上界裁剪，页数为 0 时裁剪会失效
        this.slideCount = previewer.slideCount || 0;
        // preview() 渲染的是第 1 页，据此记下已渲染页
        this.renderedPage = 1;
        // 解析期间后端可能已经推过好几页，这里无条件对齐到外部当前页。
        // goTo 内部会先判断是否已在目标页，同页不会重复渲染，所以这里不需要再判断 pageNumber > 1。
        this.goTo(this.pageNumber);
        this.$emit('loaded', this.slideCount);
      } catch (error) {
        if (seq !== this.loadSeq) {
          return;
        }
        const msg = (error && error.message) || 'PPT 加载失败，可能是跨域限制或文件不可读';
        this.errorMsg = msg;
        this.$emit('error', msg);
      } finally {
        if (seq === this.loadSeq) {
          this.loading = false;
        }
      }
    },
    /**
     * 取课件二进制流
     */
    fetchArrayBuffer(url) {
      return fetch(url, { credentials: 'omit' }).then((response) => {
        if (!response.ok) {
          throw new Error('课件下载失败（HTTP ' + response.status + '）');
        }
        return response.arrayBuffer();
      });
    },
    /**
     * 记录库算出的渲染尺寸，并同步一次缩放
     */
    applyRenderPort() {
      if (!this.previewer) {
        return;
      }
      const htmlRender = this.previewer.htmlRender;
      const renderPort = htmlRender && htmlRender.renderPort;
      const wrapper = this.previewer.wrapper;
      let width = renderPort && renderPort.width;
      let height = renderPort && renderPort.height;
      if ((!width || !height) && wrapper) {
        // 兜底：拿不到 renderPort 就量 wrapper 实际尺寸
        width = wrapper.offsetWidth;
        height = wrapper.offsetHeight;
      }
      this.baseWidth = Math.round(width || BASE_WIDTH);
      this.baseHeight = Math.round(height || BASE_WIDTH * 9 / 16);
      this.updateScale();
    },
    /**
     * 同步库自带控件的显隐
     * <p>
     * showControls 为 false（AI 上课学生端）时，只保留承载课件页的 .pptx-preview-slide-wrapper，
     * 其余子元素（上一页/下一页按钮、页码、占位）一律移除——翻页只由后端下发的 pageNumber 驱动。
     * 用结构判断而不是记 class 清单，避免库升级改样式名后失效。
     */
    syncBuiltinControls() {
      if (this.showControls) {
        return;
      }
      const wrapper = this.previewer && this.previewer.wrapper;
      if (!wrapper) {
        return;
      }
      Array.prototype.slice.call(wrapper.children).forEach((node) => {
        const isSlide = node.classList && node.classList.contains('pptx-preview-slide-wrapper');
        if (!isSlide) {
          wrapper.removeChild(node);
        }
      });
    },
    /**
     * 跳转到指定页（1 开始）
     * <p>
     * 幂等：目标页就是已渲染页时直接返回，重复调用（Vue 重复触发 watch、后端重复下发）不会闪页。
     * <p>
     * 这里刻意不用库的 previewer.currentIndex 做判断：那是库的内部状态，
     * 在「解析还没完成就收到页码」这类时序下会和实际渲染结果对不上，
     * 一旦对不上就会走「已在目标页」的早退分支，导致本该翻的页永远翻不过去。
     * 改用自己维护的 renderedPage（每次真正调完 renderSingleSlide 才更新），
     * 只要它和期望页不一致就一定重渲染，宁可多渲染一次也不会漏翻。
     */
    goTo(page) {
      if (!this.previewer || !page) {
        // 解析尚未完成：不丢弃目标页，等 loadViewer 完成后会再对齐一次（见 loadViewer）
        return;
      }
      let target = Math.max(1, page);
      if (this.slideCount) {
        target = Math.min(target, this.slideCount);
      }
      if (target === this.renderedPage) {
        return;
      }
      // 先落状态再渲染：renderSlide 内部会重建页容器 DOM，
      // 渲染过程中若再收到一次同页请求，靠这个字段直接短路，避免同页被反复重建
      this.renderedPage = target;
      this.previewer.renderSingleSlide(target - 1);
      // 换页会重渲染页容器，保险起见再同步一次控件显隐
      this.syncBuiltinControls();
    },
    /**
     * 按舞台尺寸重算缩放比例
     * <p>
     * 等比缩放到整页都看得见（contain），不裁切、不拉伸；舞台比课件大时允许放大，
     * 因为内部是 DOM/矢量渲染，放大不会变糊。
     */
    updateScale() {
      const stage = this.$refs.stage;
      if (!stage || !this.baseWidth || !this.baseHeight) {
        return;
      }
      const rect = stage.getBoundingClientRect();
      // 父级还没完成布局时尺寸是 0，这时算出来 scale=0 会把课件缩没，直接跳过
      if (rect.width < 40 || rect.height < 40) {
        return;
      }
      this.scale = Math.min(rect.width / this.baseWidth, rect.height / this.baseHeight);
    },
    /**
     * 监听容器尺寸变化
     * <p>
     * 只重算 scale（改一个 CSS 变量级别的开销），不重新解析 pptx，所以拖窗口也不卡。
     */
    setupResizeObserver() {
      if (typeof ResizeObserver === 'undefined' || !this.$refs.stage) {
        return;
      }
      let lastWidth = 0;
      let lastHeight = 0;
      this.resizeObserver = new ResizeObserver((entries) => {
        const rect = entries[0] && entries[0].contentRect;
        if (!rect || rect.width === lastWidth && rect.height === lastHeight) {
          return;
        }
        lastWidth = rect.width;
        lastHeight = rect.height;
        // 拖拽窗口会连续触发，合并成一次，避免每帧都动布局
        if (this.resizeTimer) {
          clearTimeout(this.resizeTimer);
        }
        this.resizeTimer = setTimeout(() => {
          this.resizeTimer = null;
          this.updateScale();
        }, 120);
      });
      this.resizeObserver.observe(this.$refs.stage);
    },
    teardownResizeObserver() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    reload() {
      this.loadViewer();
    },
    destroyViewer() {
      if (this.previewer) {
        try {
          this.previewer.destroy();
        } catch (e) {
          // 销毁异常不影响页面其它逻辑
        }
        this.previewer = null;
      }
      // 库的 destroy 只解绑事件，不保证清空 DOM；宿主容器自己清，避免残留旧课件节点
      if (this.$refs.container) {
        this.$refs.container.innerHTML = '';
      }
      this.slideCount = 0;
      // 页码状态必须跟着一起清：否则换了课件后 renderedPage 还留着旧值，
      // 新课件第 1 页会被判成「已在目标页」而不渲染
      this.renderedPage = 0;
      this.baseWidth = 0;
      this.baseHeight = 0;
    }
  }
};
</script>

<style scoped>
.pptx-preview {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.pptx-stage {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* 容器尺寸固定为课件原始尺寸，靠 transform 缩放居中，所以这里只定位、不做宽高自适应 */
.pptx-container {
  position: absolute;
  left: 50%;
  top: 50%;
  transform-origin: center center;
  background-color: #ffffff;
}

/* pptx-preview 渲染出的内容由第三方生成，需使用深度选择器调整 */
/* wrapper 自带固定像素宽高、黑色底和 overflow-y，这里覆盖成随容器走的透明层 */
.pptx-container >>> .pptx-preview-wrapper {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  overflow: hidden !important;
  background: #ffffff !important;
}

/* 页容器在 slide 模式下会按 viewPort 高度算 top 做垂直居中，容器尺寸已经精确，强制归零 */
.pptx-container >>> .pptx-preview-slide-wrapper {
  top: 0 !important;
  left: 0 !important;
}

.pptx-mask {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 13px;
  color: #909399;
  background-color: rgba(255, 255, 255, 0.92);
}

.pptx-mask i {
  font-size: 28px;
}

.pptx-mask-error {
  color: #f56c6c;
}

.pptx-err-text {
  max-width: 80%;
  text-align: center;
  line-height: 1.7;
  color: #909399;
}
</style>
